/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { MessageSeverity } from './TrainIcons.types';
import { MessageConfirmationIcon } from '../PRIVATE_ThemedIcons/MessageConfirmationIcon';
import { MessageErrorIcon } from '../PRIVATE_ThemedIcons/MessageErrorIcon';
import { MessageInfoIcon } from '../PRIVATE_ThemedIcons/MessageInfoIcon';
import { MessageWarningIcon } from '../PRIVATE_ThemedIcons/MessageWarningIcon';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TrainVariantOptions, TrainStyles } from './themes/TrainStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { TrainRedwoodTheme } from './themes/redwood/TrainTheme';

const messageTypeIcons = {
  confirmation: MessageConfirmationIcon,
  error: MessageErrorIcon,
  fatal: MessageErrorIcon,
  info: MessageInfoIcon,
  warning: MessageWarningIcon
};

/**
 * Props for the UserChosenIcon component
 */
type Props = {
  /**
   * Determines the message type icon from the following types 'error', 'warning', 'confirmation', 'info' and 'none'.
   */
  messageType: Exclude<MessageSeverity, 'none'>;

  /**
   * Determines if the step is disabled or not
   */
  isDisabled: boolean;

  /**
   * Translations resources
   */
  translations?: {
    /**
     * Text for 'error' severity level
     */
    error?: string;

    /**
     * Text for 'error' severity level
     */
    fatal?: string;

    /**
     * Text for 'warning' severity level
     */
    warning?: string;

    /**
     * Text for 'info' severity level
     */
    info?: string;

    /**
     * Text for 'confirmation' severity level
     */
    confirmation?: string;
  };
};

/**
 * UserChosenIcon Component for rendering the severity based icon in Message
 */
function UserChosenIcon({ messageType, isDisabled = false }: Props) {
  const IconComponent = messageTypeIcons[messageType];

  const { styles, variantClasses } = useComponentTheme<TrainVariantOptions, TrainStyles>(
    TrainRedwoodTheme,
    {
      messageType: messageType,
      stepIconDisabled: isDisabled ? 'isDisabled' : 'notDisabled'
    }
  );
  return (
    <div class={classNames([styles.messageTypeIconBaseStyle, variantClasses])} role="presentation">
      <IconComponent />
    </div>
  );
}

export { UserChosenIcon };
